import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Calendar, Eye, Image, Heart, Send, TrendingUp, Loader2, MapPin } from 'lucide-react';
import { fetchBuyerActivity } from '../api/analyticsApi';

const BuyerActivityModal = ({ isOpen, onClose, buyerId, buyerEmail }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && buyerId) {
            loadBuyerActivity();
        }
    }, [isOpen, buyerId]);

    const loadBuyerActivity = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await fetchBuyerActivity(buyerId);
            setData(result);
        } catch (err) {
            console.error('Error loading buyer activity:', err);
            setError('Failed to load buyer activity');
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getEventIcon = (eventName) => {
        const icons = {
            'property_viewed': Eye,
            'property_gallery_viewed': Image,
            'property_saved': Heart,
            'enquiry_sent': Send,
            'call_clicked': Phone,
            'email_clicked': Mail,
            'signup_completed': User,
        };
        return icons[eventName] || TrendingUp;
    };

    const getEventColor = (eventName) => {
        const colors = {
            'property_viewed': 'text-blue-600 bg-blue-50',
            'property_gallery_viewed': 'text-purple-600 bg-purple-50',
            'call_clicked': 'text-green-600 bg-green-50',
            'email_clicked': 'text-orange-600 bg-orange-50',
            'enquiry_sent': 'text-red-600 bg-red-50',
            'property_saved': 'text-pink-600 bg-pink-50',
            'signup_completed': 'text-indigo-600 bg-indigo-50',
        };
        return colors[eventName] || 'text-gray-600 bg-gray-50';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Buyer Activity Timeline</h2>
                        <p className="text-sm text-gray-600 mt-1">{buyerEmail || `Buyer ID: ${buyerId}`}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : !data ? (
                        <div className="text-center text-gray-500 py-8">No data available</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                            {/* Buyer Info & Stats - Left Sidebar */}
                            <div className="lg:col-span-1 space-y-4">
                                {/* Buyer Info Card */}
                                <div className="bg-white rounded-lg border border-gray-200 p-5">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-12 h-12 bg-[#55C5D0] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                            {data.buyer.name?.charAt(0).toUpperCase() || data.buyer.email?.charAt(0).toUpperCase() || 'U'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-900 truncate">{data.buyer.name || 'Unknown'}</h3>
                                            <p className="text-sm text-gray-600 truncate">{data.buyer.email}</p>
                                        </div>
                                    </div>
                                    {data.buyer.phone && (
                                        <div className="text-sm text-gray-700 flex items-center gap-2 mt-3">
                                            <Phone className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">{data.buyer.phone}</span>
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-3">
                                        Member since: {formatTimestamp(data.buyer.created_at).split(',')[0]}
                                    </div>
                                </div>

                                {/* Stats Cards */}
                                <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
                                    <h4 className="font-semibold text-gray-900 text-sm mb-3">Activity Stats</h4>
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Total Events</span>
                                            <span className="font-semibold text-gray-900">{data.stats.total_events}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Property Views</span>
                                            <span className="font-semibold text-gray-900">{data.stats.property_views}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Gallery Views</span>
                                            <span className="font-semibold text-gray-900">{data.stats.gallery_views}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Properties Saved</span>
                                            <span className="font-semibold text-gray-900">{data.stats.properties_saved}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Enquiries Sent</span>
                                            <span className="font-semibold text-red-600">{data.stats.enquiries_sent}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-600">Intent Clicks</span>
                                            <span className="font-semibold text-green-600">{data.stats.intent_clicks}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline - Right Side */}
                            <div className="lg:col-span-3">
                                <h4 className="font-semibold text-gray-900 mb-4 text-lg">Activity Timeline</h4>
                                <div className="space-y-3 pr-2">
                                    {data.timeline.map((event, index) => {
                                        const Icon = getEventIcon(event.event_name);
                                        const colorClass = getEventColor(event.event_name);
                                        
                                        return (
                                            <div
                                                key={event.event_id}
                                                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                                            >
                                                <div className="flex items-start gap-4">
                                                    <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-1 gap-4">
                                                            <h5 className="font-semibold text-gray-900 text-sm">
                                                                {event.event_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                            </h5>
                                                            <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                                                                {formatTimestamp(event.occurred_at)}
                                                            </span>
                                                        </div>
                                                        
                                                        {event.property && (
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                <span className="text-sm text-gray-700 truncate">
                                                                    {event.property.street_address || event.property.address || event.property.title}
                                                                </span>
                                                                {event.property.suburb && (
                                                                    <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                                                                        ({event.property.suburb})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        {data && `${data.timeline.length} activities recorded`}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-[#55C5D0] text-white rounded-lg font-medium hover:bg-[#4AB5C0] transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuyerActivityModal;
